import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';
import Panel, { PanelVariant } from './Panel';

export const dialogVariants = ['default', 'confirm', 'alert', 'dialogue'] as const;
export type DialogVariant = (typeof dialogVariants)[number];

interface DialogProps {
  open: boolean;
  title?: string;
  variant?: DialogVariant;
  onClose?: () => void;
  children: React.ReactNode;
}

function panelVariant(variant: DialogVariant): PanelVariant {
  switch (variant) {
    case 'dialogue':
      return 'light';
    case 'default':
    case 'confirm':
    case 'alert':
      return 'default';
    default: {
      const neverVariant: never = variant;
      return neverVariant;
    }
  }
}

function dialogClass(variant: DialogVariant): string {
  switch (variant) {
    case 'default':
      return 'osrs-dialog';
    case 'confirm':
      return 'osrs-dialog osrs-dialog--confirm';
    case 'alert':
      return 'osrs-dialog osrs-dialog--alert';
    case 'dialogue':
      return 'osrs-dialog osrs-dialog--dialogue';
    default: {
      const neverVariant: never = variant;
      return neverVariant;
    }
  }
}

const Dialog: React.FC<DialogProps> = ({
  open,
  title,
  variant = 'default',
  onClose,
  children,
}) => {
  const titleId = useId();

  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="osrs-dialog-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cx(dialogClass(variant))}
        onClick={(event) => event.stopPropagation()}
      >
        <Panel
          title={title}
          titleId={title ? titleId : undefined}
          variant={panelVariant(variant)}
          onClose={onClose}
        >
          {children}
        </Panel>
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
